import type { ComponentType } from 'react';
import type { LucideIcon } from 'lucide-react';

export type AppId =
  | 'home'
  | 'terminal'
  | 'files'
  | 'notepad'
  | 'knowledge'
  | 'git'
  | 'sessions'
  | 'office'
  | 'excel'
  | 'powerpoint'
  | 'pdf'
  | 'mail'
  | 'calendar'
  | 'browser'
  | 'app-center'
  | 'operator'
  | 'tasks'
  | 'docker'
  | 'code'
  | 'codex'
  | 'dsh'
  | 'system'
  | 'settings';

export interface SessionInfo {
  user: string;
  csrf: string;
  deviceId: string;
  capabilities: string[];
}

export interface Bounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface WindowState {
  id: string;
  appId: AppId;
  title: string;
  desktopId: string;
  bounds: Bounds;
  restoreBounds?: Bounds;
  minimized: boolean;
  maximized: boolean;
  zIndex: number;
}

export interface VirtualDesktop {
  id: string;
  name: string;
}

export interface SnapGroup {
  id: string;
  desktopId: string;
  windowIds: [string, string];
}

export interface WorkspaceState {
  schemaVersion: 2 | 3;
  windows: WindowState[];
  activeWindowId: string | null;
  desktopId: string;
  desktops: VirtualDesktop[];
  snapGroups?: SnapGroup[];
}

export interface WebosSettings {
  schemaVersion: 5;
  shellLayout: 'windows';
  terminalFontSize: number;
  uiScale: number;
  taskbarAlignment: 'center' | 'left';
  reducedTransparency: boolean;
  clock24Hour: boolean;
  highContrast: boolean;
  reducedMotion: boolean;
  doNotDisturb: boolean;
  operatorUiLocale: {
    schemaVersion: 1;
    value: 'de-CH' | 'en-GB' | 'id-ID' | 'ja-JP';
    version: number;
    updatedAt: string;
  };
}

export const DEFAULT_WEBOS_SETTINGS: WebosSettings = {
  schemaVersion: 5,
  shellLayout: 'windows',
  terminalFontSize: 14,
  uiScale: 1,
  taskbarAlignment: 'center',
  reducedTransparency: false,
  clock24Hour: true,
  highContrast: false,
  reducedMotion: false,
  doNotDisturb: false,
  operatorUiLocale: {
    schemaVersion: 1,
    value: 'de-CH',
    version: 1,
    updatedAt: '2026-08-15T00:00:00.000Z'
  }
};

export interface WebosNotification {
  id: string;
  title: string;
  message: string;
  tone: 'info' | 'success' | 'warning' | 'danger';
  actionApp: AppId | null;
  actionLabel: string | null;
  readAt: string | null;
  createdAt: string;
}

export interface UniversalSearchResult {
  id: string;
  kind: 'file' | 'directory' | 'nextcloud' | 'project' | 'mail';
  title: string;
  subtitle: string;
  appId: AppId;
  root?: string;
  path?: string;
}

export interface HostTask {
  pid: number;
  ppid: number;
  user: string;
  cpuPercent: number;
  memoryPercent: number;
  rssBytes: number;
  state: string;
  elapsedSeconds: number;
  name: string;
}

export interface TaskSnapshot {
  generatedAt: string;
  hostname: string;
  uptimeSeconds: number;
  loadAverage: [number, number, number];
  cpuCount: number;
  processCount: number;
  memory: { totalBytes: number; usedBytes: number; availableBytes: number; cachedBytes: number; swapTotalBytes: number; swapFreeBytes: number; usedPercent: number };
  processes: HostTask[];
}

export interface TerminalPane { id: string; active: boolean; command: string; directory?: string; repository?: string; branch?: string }
export interface TerminalWindow { id: string; index: number; name: string; active: boolean; panes: TerminalPane[] }
export interface TerminalLayout { session: string; windows: TerminalWindow[] }
export type TerminalAction =
  | 'newTab'
  | 'selectTab'
  | 'renameTab'
  | 'moveTabBefore'
  | 'moveTabAfter'
  | 'closeTab'
  | 'selectPane'
  | 'splitHorizontal'
  | 'splitVertical'
  | 'joinTabHorizontal'
  | 'joinTabVertical'
  | 'breakPane'
  | 'closePane'
  | 'layoutEvenHorizontal'
  | 'layoutEvenVertical'
  | 'layoutMainHorizontal'
  | 'layoutMainVertical'
  | 'layoutTiled';

export type ContainerAction = 'start' | 'stop' | 'restart';

export interface ControlledContainer {
  name: string;
  id: string;
  image: string;
  state: string;
  status: string;
  project: string;
  createdAt: string;
  ports: string;
  cpuPercent: string;
  memoryUsage: string;
  memoryPercent: string;
  networkIo: string;
  blockIo: string;
  pids: string;
  controllable: boolean;
}

export interface AppAvailability {
  id: AppId;
  available: boolean;
}

export type AppCenterKind = 'web' | 'nextcloud' | 'operator';
export type AppCenterInstallState = 'available' | 'blocked' | 'installed' | 'update-available';

export interface AppCenterPermission {
  id: string;
  label: string;
  description: string;
  required: boolean;
}

export interface AppCenterSecurity {
  source: string;
  auditedAt: string;
  launchTarget: string;
  isolation: 'isolated-browser' | 'nextcloud' | 'operator-gateway';
}

export interface AppCenterApp {
  id: string;
  name: string;
  summary: string;
  description: string;
  publisher: string;
  version: string;
  category: string;
  kind: AppCenterKind;
  icon: string;
  license: string;
  sourceUrl: string;
  launchOrigin: string;
  launchMode: 'isolated-browser' | 'unavailable';
  permissions: AppCenterPermission[];
  security: AppCenterSecurity;
  status: AppCenterInstallState;
  curated: boolean;
  verified: boolean;
  blockedReason: string | null;
  installed: boolean;
  installedVersion: string | null;
  installedAt: string | null;
  updatedAt: string | null;
  compatible: boolean;
  featured: boolean;
}

export interface AppCenterCatalog {
  schemaVersion: 1;
  catalogRevision: string;
  generatedAt: string;
  policy: {
    curatedOnly: true;
    arbitraryRepositories: false;
    launchIsolation: 'browserless';
    containerInstallsEnabled: false;
  };
  apps: AppCenterApp[];
}

export interface GitProject {
  id: string;
  name: string;
  canonical: string;
  branch: string;
  dirty: boolean;
  changeCount: number;
  writable: boolean;
  forgejoCanonical: boolean;
}

export interface GitChange {
  path: string;
  originalPath?: string;
  status: string;
  staged: boolean;
  workingTree: boolean;
  untracked: boolean;
  conflicted: boolean;
}

export interface GitCommit {
  hash: string;
  shortHash: string;
  date: string;
  author: string;
  subject: string;
}

export interface GitRepositoryStatus {
  id: string;
  name: string;
  canonical: string;
  branch: string;
  upstream: string;
  ahead: number;
  behind: number;
  head: string;
  headShort: string;
  remote: string;
  forgejoCanonical: boolean;
  dirty: boolean;
  counts: { total: number; staged: number; modified: number; untracked: number; conflicted: number };
  changes: GitChange[];
  commits: GitCommit[];
}

export interface AppRuntimeProps {
  windowId: string;
  active: boolean;
  user: string;
  launchApp: (appId: AppId) => void;
  launchManagedApp: (appId: string) => Promise<void>;
  closeWindow: (windowId: string) => void;
  settings: WebosSettings;
  updateSettings: (patch: Partial<WebosSettings>) => Promise<void>;
  reportStatus: (message: string, tone?: 'info' | 'success' | 'warning' | 'danger') => void;
}

export interface AppDefinition {
  id: AppId;
  name: string;
  description: string;
  icon: LucideIcon;
  component: ComponentType<AppRuntimeProps>;
  defaultBounds: Bounds;
  singleton: boolean;
  accent: string;
}

export interface ContextAction {
  id: string;
  label: string;
  icon?: LucideIcon;
  disabled?: boolean;
  danger?: boolean;
  separator?: boolean;
  run: () => void;
}

export interface ContextMenuState {
  x: number;
  y: number;
  actions: ContextAction[];
}

export interface FileRoot {
  id: string;
  label: string;
  writable: boolean;
  provider?: 'local' | 'nextcloud';
  connected?: boolean;
}

export interface FilePlace {
  root: string;
  path: string;
  label: string;
  type: 'file' | 'directory';
  updatedAt: string;
}

export interface FileEntry {
  name: string;
  path: string;
  type: 'file' | 'directory';
  size: number;
  modified: string;
  etag?: string;
  mimeType?: string;
  originalPath?: string;
  deletedAt?: string;
  permissions?: string;
  writable?: boolean;
  canWrite?: boolean;
  canDelete?: boolean;
  canRename?: boolean;
  canMove?: boolean;
  canCopy?: boolean;
  canCreateFile?: boolean;
  canCreateDirectory?: boolean;
  protection?: 'technical' | 'company-root';
}

export type OfficeKind = 'word' | 'cell' | 'slide' | 'pdf';

export interface OfficeDocument {
  name: string;
  kind: OfficeKind;
  extension: string;
  size: number;
  modified: string;
  editable: boolean;
}

export interface MailAccount {
  id: string;
  email: string;
  displayName: string;
  username: string;
}

export interface MailFolder {
  path: string;
  name: string;
  specialUse: string | null;
  total: number;
  unseen: number;
}

export interface MailMessageSummary {
  uid: number;
  subject: string;
  from: Array<{ name: string; address: string }>;
  to: Array<{ name: string; address: string }>;
  date: string;
  size: number;
  unread: boolean;
  flagged: boolean;
  hasAttachments: boolean;
}

export interface MailMessageDetail {
  uid: number;
  subject: string;
  from: Array<{ name?: string; address?: string }>;
  to: Array<{ name?: string; address?: string }>;
  cc: Array<{ name?: string; address?: string }>;
  date: string;
  text: string;
  html: string;
  attachments: Array<{ id: string; filename: string; contentType: string; size: number }>;
}

export interface PimCalendar {
  id: string;
  name: string;
  color: string;
  readOnly: boolean;
  supportsEvents: boolean;
  supportsTasks: boolean;
}

export interface PimEvent {
  id: string;
  calendarId: string;
  uid: string;
  etag: string;
  title: string;
  description: string;
  location: string;
  start: string;
  end: string;
  allDay: boolean;
  recurring: boolean;
  occurrenceId: string;
}

export interface PimEventInput {
  calendarId: string;
  uid?: string;
  etag?: string;
  title: string;
  description: string;
  location: string;
  start: string;
  end: string;
  allDay: boolean;
}

export interface PimTask {
  id: string;
  calendarId: string;
  uid: string;
  etag: string;
  title: string;
  description: string;
  due: string | null;
  completed: boolean;
  percentComplete: number;
}

export interface PimTaskInput {
  calendarId: string;
  uid?: string;
  etag?: string;
  title: string;
  description: string;
  due: string | null;
  completed: boolean;
  percentComplete: number;
}

export interface PimAddressBook {
  id: string;
  name: string;
  readOnly: boolean;
}

export interface PimContact {
  id: string;
  addressBookId: string;
  uid: string;
  etag: string;
  displayName: string;
  givenName: string;
  familyName: string;
  organization: string;
  emails: string[];
  phones: string[];
}

export interface PimContactInput {
  addressBookId: string;
  uid?: string;
  etag?: string;
  displayName: string;
  givenName: string;
  familyName: string;
  organization: string;
  emails: string[];
  phones: string[];
}

export interface CodexWorkspace {
  id: string;
  projectId: string;
  branch: string;
  baseRevision: string;
  createdAt: string;
  updatedAt?: string;
  exists: boolean;
}

export interface CodexWorkspaceStatus {
  workspaceId: string;
  branch: string;
  head: string;
  status: string;
  diff: string;
  pushAvailable: boolean;
}

export interface CodexThreadSummary {
  threadId: string;
  workspaceId: string;
  title: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}
